import {
  ChartConfig,
  ChartType,
  Granularity,
  MetricChartLine,
  MetricsBreakdown,
  TimePeriod,
} from '../types';

export const creditChartLines: MetricChartLine[] = [
  { dataKey: 'inboundCredits', name: 'Inbound Credits', color: '#2563eb' },
  { dataKey: 'outboundCredits', name: 'Outbound Credits', color: '#16a34a' },
  { dataKey: 'contactValidationCredits', name: 'Contact Validation Credits', color: '#dc2626' },
];

export const messageTypeLines: MetricChartLine[] = [
  { dataKey: 'smsCount', name: 'SMS Message Count', color: '#0891b2' },
  { dataKey: 'mmsCount', name: 'MMS Message Count', color: '#f59e0b' },
];

export const campaignInboxLines: MetricChartLine[] = [
  { dataKey: 'campaignMessagesCount', name: 'Campaign Messages', color: '#4f46e5' },
  { dataKey: 'inboxMessagesCount', name: 'Inbox Messages', color: '#be123c' },
];

export const allChartLines: MetricChartLine[] = [
  ...creditChartLines,
  ...messageTypeLines,
  ...campaignInboxLines,
];

export const CREDIT_CHART_TYPES: ChartType[] = ['area', 'line', 'bar'];
export const LINE_PIE_TYPES: ChartType[] = ['line', 'pie'];
export const GRANULARITIES: Granularity[] = ['day', 'week', 'month', 'year'];

export const CREDIT_CHART_STORAGE_KEY = 'home.chart.credits';
export const MESSAGE_TYPE_STORAGE_KEY = 'home.chart.messageType';
export const CAMPAIGN_INBOX_STORAGE_KEY = 'home.chart.campaignInbox';
export const GRANULARITY_STORAGE_KEY = 'home.granularity';

export const DEFAULT_CREDIT_CONFIG: ChartConfig = { type: 'area' };
export const DEFAULT_MESSAGE_TYPE_CONFIG: ChartConfig = { type: 'pie' };
export const DEFAULT_CAMPAIGN_INBOX_CONFIG: ChartConfig = { type: 'pie' };

export const loadChartConfig = (
  key: string,
  fallback: ChartConfig,
  allowedTypes: ChartType[],
): ChartConfig => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<ChartConfig>;
    const type =
      typeof parsed.type === 'string' && allowedTypes.includes(parsed.type as ChartType)
        ? (parsed.type as ChartType)
        : fallback.type;
    return { type };
  } catch {
    return fallback;
  }
};

export const saveChartConfig = (key: string, config: ChartConfig): void => {
  try {
    window.localStorage.setItem(key, JSON.stringify(config));
  } catch {
    /* ignore quota / unavailable storage */
  }
};

export const loadGranularity = (): Granularity => {
  try {
    const raw = window.localStorage.getItem(GRANULARITY_STORAGE_KEY);
    return raw && (GRANULARITIES as string[]).includes(raw) ? (raw as Granularity) : 'week';
  } catch {
    return 'week';
  }
};

// Sensible default bucket size per look-back period. Longer windows roll up
// coarser so the charts stay readable (365d -> monthly, 90d -> weekly).
export const defaultGranularityForPeriod = (period: TimePeriod): Granularity => {
  switch (period) {
    case TimePeriod.LAST365DAYS:
      return 'month';
    case TimePeriod.LAST90DAYS:
      return 'week';
    case TimePeriod.LAST30DAYS:
      return 'week';
    default:
      return 'day';
  }
};

// Backend breakdown only supports day/week/month; collapse 'year' onto 'month'.
export const granularityToBreakdown = (granularity: Granularity): MetricsBreakdown =>
  granularity === 'year' ? 'month' : granularity;

export const saveGranularity = (granularity: Granularity): void => {
  try {
    window.localStorage.setItem(GRANULARITY_STORAGE_KEY, granularity);
  } catch {
    /* ignore */
  }
};
